import type { CategoryTreeRow } from './types';

import type { Category, ProductWithStock } from '@/features/products/types';

/** Real counts only — how many of the merchant's own fetched products carry this category's id, never a stored or estimated figure. */
export function countProductsByCategory(
  products: readonly ProductWithStock[],
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const product of products) {
    if (product.categoryId === null) continue;
    counts.set(product.categoryId, (counts.get(product.categoryId) ?? 0) + 1);
  }
  return counts;
}

/**
 * Flattens the self-referencing tree (`parentCategoryId`) into ordered
 * rows carrying their own depth, for indented table rendering — the same
 * parent/child walk the storefront's `CategoryList.tsx` (`buildTree`)
 * already does for its own tree, reimplemented here for this app's
 * `Category` shape rather than cross-imported (`apps/storefront` is a
 * separate Next.js app boundary).
 *
 * A category whose `parentCategoryId` names a row not present in this
 * list (structurally impossible today — `CategoryRepository.softDelete`
 * refuses to remove a category that still has live children — but not
 * worth trusting blindly) is rendered as a root rather than dropped, so
 * no category ever silently disappears from the screen.
 */
export function flattenCategoryTree(
  categories: readonly Category[],
  productCounts: ReadonlyMap<string, number>,
): readonly CategoryTreeRow[] {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const byParent = new Map<string | null, Category[]>();

  for (const category of categories) {
    const parentKey =
      category.parentCategoryId !== null && byId.has(category.parentCategoryId)
        ? category.parentCategoryId
        : null;
    const siblings = byParent.get(parentKey) ?? [];
    siblings.push(category);
    byParent.set(parentKey, siblings);
  }

  for (const siblings of byParent.values()) {
    siblings.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
  }

  const rows: CategoryTreeRow[] = [];

  function walk(parentId: string | null, depth: number): void {
    for (const category of byParent.get(parentId) ?? []) {
      rows.push({
        category,
        depth,
        productCount: productCounts.get(category.id) ?? 0,
      });
      walk(category.id, depth + 1);
    }
  }

  walk(null, 0);
  return rows;
}
